import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatMatchDateTimeLabel } from '../../utils/dateLocale';
import { ScoreText } from '../ui/ScoreText';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Calendar, Clock, Eye, Lock, PencilLine, Target } from 'lucide-react';
import { getTeamDisplayName } from '../../utils/teamDisplayName';

/**
 * Upcoming game row for the upcoming-games page (navigate to place bet / view bets).
 */
export function UpcomingGameListCard({
  game,
  basePath,
  userCanBet,
  hasUserBet,
  gameStatus
}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const home = typeof game.homeGroupId === 'object' ? game.homeGroupId : {};
  const away = typeof game.awayGroupId === 'object' ? game.awayGroupId : {};

  const statusClass =
    gameStatus.status === 'live'
      ? 'bg-amber-100 text-amber-900'
      : 'bg-brand-100 text-brand-800';

  return (
    <Card
      className={
        userCanBet
          ? 'ring-1 ring-brand-200/80'
          : 'bg-zinc-50/80 ring-1 ring-zinc-200'
      }
      hover
    >
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 flex-1 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            {game.round ? (
              <span className="rounded-full bg-zinc-100 px-2.5 py-0.5 text-xs font-semibold text-zinc-600">
                {game.round}
              </span>
            ) : null}
            <span
              className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${statusClass}`}
            >
              {gameStatus.text}
            </span>
            {game.isBettingLocked ? (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700">
                <Lock className="h-3.5 w-3.5" />
                {t('bettingLocked')}
              </span>
            ) : null}
          </div>

          <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 rounded-2xl bg-zinc-50 p-4 ring-1 ring-zinc-100 sm:gap-4">
            <TeamFace logo={home.logoUrl} name={getTeamDisplayName(home, language)} />
            <span className="text-center text-xs font-bold uppercase tracking-widest text-zinc-400">
              {t('vs')}
            </span>
            <TeamFace logo={away.logoUrl} name={getTeamDisplayName(away, language)} />
          </div>

          <div className="flex flex-wrap items-center gap-3 text-sm text-zinc-600">
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="h-4 w-4 text-brand-600" />
              <time dateTime={`${game.date}T${game.hour}`} className="tabular-nums">
                {formatMatchDateTimeLabel(game.date, game.hour, language)}
              </time>
            </span>
          </div>

          {user ? (
            <div className="pt-1">
              {hasUserBet ? (
                <p className="inline-flex flex-wrap items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-900 ring-1 ring-emerald-200/80">
                  <Target className="h-4 w-4 shrink-0" />
                  <span>
                    {language === 'en' ? 'Your prediction:' : 'הניבוי שלך:'}{' '}
                    <ScoreText
                      score={game.userBet.predictedResult}
                      className="font-semibold"
                    />
                  </span>
                </p>
              ) : userCanBet ? (
                <p className="inline-flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm font-medium text-amber-900 ring-1 ring-amber-200/80">
                  <Clock className="h-4 w-4 shrink-0" />
                  {language === 'en' ? 'No bet placed yet' : 'עדיין לא הימרת'}
                </p>
              ) : (
                <p className="inline-flex items-center gap-2 rounded-lg bg-zinc-100 px-3 py-2 text-sm font-medium text-zinc-600">
                  <Lock className="h-4 w-4 shrink-0" />
                  {language === 'en' ? 'Betting closed' : 'ההימורים נסגרו'}
                </p>
              )}
            </div>
          ) : null}
        </div>

        <div className="flex shrink-0 flex-col gap-2 sm:flex-row lg:flex-col">
          {user && userCanBet && !hasUserBet ? (
            <Button
              size="sm"
              className="w-full sm:w-auto lg:w-full"
              onClick={() => navigate(`${basePath}/games/${game._id}/bet`)}
            >
              <Target className="h-4 w-4" />
              {language === 'en' ? 'Place bet' : 'הימר'}
            </Button>
          ) : null}
          {user && userCanBet && hasUserBet ? (
            <Button
              size="sm"
              variant="success"
              className="w-full sm:w-auto lg:w-full"
              onClick={() => navigate(`${basePath}/games/${game._id}/bet`)}
            >
              <PencilLine className="h-4 w-4" />
              {language === 'en' ? 'Edit bet' : 'ערוך הימור'}
            </Button>
          ) : null}
          <Button
            size="sm"
            variant="secondary"
            className="w-full sm:w-auto lg:w-full"
            onClick={() => navigate(`${basePath}/games/${game._id}/bets`)}
          >
            <Eye className="h-4 w-4" />
            {language === 'en' ? 'All bets' : 'כל ההימורים'}
          </Button>
        </div>
      </div>
    </Card>
  );
}

function TeamFace({ logo, name }) {
  return (
    <div className="flex min-w-0 flex-col items-center gap-2 text-center sm:flex-row sm:gap-3">
      <img
        src={logo || '/default-team.png'}
        alt=""
        className="h-10 w-10 shrink-0 rounded-full border-2 border-white object-cover shadow ring-2 ring-zinc-100 sm:h-11 sm:w-11"
      />
      <span className="line-clamp-2 text-sm font-bold text-zinc-900 sm:text-base">{name || '—'}</span>
    </div>
  );
}
